import type { Lang } from '../i18n'
import { orgLines } from './privacy'

/**
 * The contact page, sv + en.
 *
 * One address and no form. A form would need a backend, and the whole point of Ronin is
 * that there is not one; it would also collect what someone typed before they decided to
 * send it. E-mail leaves that choice with the reader.
 *
 * Two things on this page are not style choices:
 *
 *  - the care section. Ronin is not a care provider and this page must never read like a
 *    way to get medical help. It says so, and it points at the emergency number.
 *  - "do not send your diary". Support does not need health data to answer a question
 *    about a button, and the privacy notice promises we hold none. Inviting people to
 *    paste it in would quietly make that untrue.
 *
 * The self-service list has to stay in step with the app: every line is something that
 * exists in Settings today. Check it when Settings changes.
 */

export type ContactCopy = {
  pageTitle: string
  lede: string

  emailTitle: string
  emailBody: string
  emailNote: string

  careTitle: string
  careBody: string

  writeTitle: string
  writeBody: string
  writePoints: string[]

  selfTitle: string
  selfBody: string
  selfPoints: string[]

  legalTitle: string
  legalBody: string
  privacyLinkLabel: string
  termsLinkLabel: string

  orgTitle: string
  orgLines: string[]
}

const en: ContactCopy = {
  pageTitle: 'Contact',
  lede: 'One address, and a person reads it. Write about anything to do with Ronin: something that does not work, something that is missing, or a question about what the app does with your data.',

  emailTitle: 'Write to us',
  emailBody: 'This is the only way to reach us, and it is enough. There is no form on this page, because a form would need a server and Ronin does not have one.',
  emailNote: 'We answer in Swedish and English. Ronin is free and made by a small team, so it can take a few days. Everything gets read.',

  careTitle: 'If it is about your health, not the app',
  careBody: 'We are not a care provider and we cannot answer questions about your treatment, your values or your doses. Those go to your care team, who know you. If you feel unwell or something is going wrong right now, call the emergency number where you are, 112 in Sweden. Do not wait for an e-mail.',

  writeTitle: 'What helps us help you',
  writeBody: 'If something is broken, these four lines usually get us to the answer in one reply instead of four:',
  writePoints: [
    'which phone you have, and which version of iOS or Android,',
    'which version of Ronin: Settings, then About Ronin, shows it,',
    'what you did, and what happened instead of what you expected,',
    'whether it happens every time or just happened once.',
  ],

  selfTitle: 'Things you can do without us',
  selfBody: 'Some of the most common questions have an answer inside the app, and they are faster than an e-mail:',
  selfPoints: [
    'Get your entries out: Settings, then export the last 30 or 90 days as a CSV file. It opens in Excel and Numbers, and you choose where it goes.',
    'Delete everything: Settings, then Delete all data. That removes every entry, every plate and every food you added, on that phone, and it cannot be undone.',
    'Change language, glucose unit, appearance or the names of your insulins: all of them are in Settings, and none of them touch what you have already written down.',
    'Keep the diary out of your phone’s backup: that is a setting in the phone itself, not in Ronin.',
  ],

  legalTitle: 'Privacy and terms',
  legalBody: 'Questions about what Ronin stores, what the stores see, or how to have something deleted are answered on these two pages before you write. If they do not answer yours, the address above does.',
  privacyLinkLabel: 'Privacy',
  termsLinkLabel: 'Terms and conditions',

  orgTitle: 'Who publishes Ronin',
  orgLines: orgLines('en'),
}

const sv: ContactCopy = {
  pageTitle: 'Kontakt',
  lede: 'En adress, och en människa läser den. Skriv om vad som helst som rör Ronin: något som inte fungerar, något som saknas, eller en fråga om vad appen gör med dina uppgifter.',

  emailTitle: 'Skriv till oss',
  emailBody: 'Det här är enda vägen till oss, och den räcker. Det finns inget formulär på sidan, för ett formulär skulle behöva en server och Ronin har ingen.',
  emailNote: 'Vi svarar på svenska och engelska. Ronin är gratis och görs av ett litet gäng, så det kan ta några dagar. Allt blir läst.',

  careTitle: 'Handlar det om din hälsa och inte om appen',
  careBody: 'Vi är inte vårdgivare och kan inte svara på frågor om din behandling, dina värden eller dina doser. De frågorna går till din vårdkontakt, som känner dig. Mår du dåligt eller håller något på att gå fel just nu: ring 112. Vänta inte på ett mejl.',

  writeTitle: 'Det här hjälper oss att hjälpa dig',
  writeBody: 'Om något är trasigt brukar de här fyra raderna räcka för ett svar på första försöket i stället för det fjärde:',
  writePoints: [
    'vilken telefon du har, och vilken version av iOS eller Android,',
    'vilken version av Ronin: Inställningar, sedan Om Ronin, visar den,',
    'vad du gjorde, och vad som hände i stället för det du väntade dig,',
    'om det händer varje gång eller hände en enda gång.',
  ],

  selfTitle: 'Sådant du kan göra utan oss',
  selfBody: 'En del av de vanligaste frågorna har ett svar inne i appen, och det går fortare än ett mejl:',
  selfPoints: [
    'Få ut dina inlägg: Inställningar, sedan exportera de senaste 30 eller 90 dagarna som en CSV-fil. Den öppnas i Excel och Numbers, och du väljer vart den går.',
    'Ta bort allt: Inställningar, sedan Ta bort all data. Det raderar varje inlägg, varje tallrik och varje mat du lagt till i den telefonen, och det går inte att ångra.',
    'Byta språk, glukosenhet, utseende eller namnen på dina insuliner: allt finns under Inställningar, och inget av det rör det du redan skrivit ner.',
    'Hålla dagboken utanför telefonens säkerhetskopia: det är en inställning i telefonen, inte i Ronin.',
  ],

  legalTitle: 'Integritet och villkor',
  legalBody: 'Frågor om vad Ronin sparar, vad butikerna ser eller hur du får något raderat är besvarade på de här två sidorna innan du skriver. Svarar de inte på din fråga gör adressen ovan det.',
  privacyLinkLabel: 'Integritet',
  termsLinkLabel: 'Villkor',

  orgTitle: 'Vem som ger ut Ronin',
  orgLines: orgLines('sv'),
}

export const contactCopy: Record<Lang, ContactCopy> = { sv, en }
